import { useEffect } from 'react';
import { initOutbox } from '@/lib/sync';
import { initBookmarkOutbox } from '@/lib/sync-bookmarks';
import { initHighlightOutbox } from '@/lib/sync-highlights';
import {
  hydrateQueueStore,
  processQueueInBackground,
  startQueueListScope,
} from '@/lib/audio-queue';
import { useAdiaStore } from '@/lib/store';
import { useBookmarkStore } from '@/lib/bookmark-store';
import { useHighlightStore } from '@/lib/highlight-store';
import {
  BIBLE_BOOKMARKS_ENABLED,
  BIBLE_READER_V2_ENABLED,
} from '@/lib/feature-flags';
import { KEEPSAKES_ENABLED } from '@/lib/core-cutover';
import { initKeepsakeFences } from '@/lib/keepsake/fences';
import {
  requestContentLifecycleBootstrapSync,
  setContentLifecycleOwnerRuntimeEnabled,
} from '@/lib/content-lifecycle/sync';

/**
 * Starts every owner-bearing background reader/writer only after AuthGate and
 * OwnerGate have both passed. A fresh profile therefore binds before its first
 * pull, and an account mismatch cannot hydrate, transcribe, enrich, or mutate
 * the prior account's active local state behind the protective screen.
 */
export default function OwnerScopedRuntime() {
  useEffect(() => {
    let disposed = false;
    setContentLifecycleOwnerRuntimeEnabled(true);

    if (KEEPSAKES_ENABLED) {
      initKeepsakeFences();
    }

    initOutbox({
      bookmarks: BIBLE_BOOKMARKS_ENABLED,
      highlights: BIBLE_READER_V2_ENABLED,
    });
    requestContentLifecycleBootstrapSync();

    if (BIBLE_BOOKMARKS_ENABLED) {
      initBookmarkOutbox();
      void useBookmarkStore.getState().loadBookmarks().catch(() => {});
    }
    if (BIBLE_READER_V2_ENABLED) {
      initHighlightOutbox();
      void useHighlightStore.getState().loadHighlights().catch(() => {});
    }

    // Every effect run (a genuine mount or StrictMode's replay) owns a fresh
    // queue-list scope, opened once auth has resolved; the hydrate below
    // fills only that scope.
    const queueListScope = startQueueListScope();
    void queueListScope.ready
      // Only this run's own scope is hydrated; a run torn down before auth
      // resolved opens nothing and must not fill a newer run's list.
      .then((opened) => (opened && !disposed ? hydrateQueueStore() : undefined))
      .then(() => (disposed ? undefined : useAdiaStore.getState().loadPrayers()))
      .catch(() => {})
      .then(() => {
        if (!disposed) processQueueInBackground();
      });

    const onOnline = () => {
      if (disposed) return;
      processQueueInBackground();
    };
    const onVisible = () => {
      if (disposed || document.visibilityState !== 'visible') return;
      processQueueInBackground();
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      setContentLifecycleOwnerRuntimeEnabled(false);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
      queueListScope.close();
    };
  }, []);

  return null;
}
